import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Drawer, EmptyState } from '@/components/campus';
import {
  User,
  Users as UsersIcon,
  Mail,
  Building2,
  Calendar,
  Shield,
  MoreHorizontal,
  Eye,
  Loader2,
  Search,
  Ban,
  RefreshCw,
  Trash2,
  Key,
  UserCheck,
  Crown,
  AlertTriangle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format helpers
const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Role display - shows "President" for owner
const getRoleDisplay = (role) => {
  if (role === 'owner') return 'President';
  return role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Member';
};

// Role badge colors
const getRoleBadgeClass = (role) => {
  const classes = {
    owner: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    admin: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    event_manager: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    door_staff: 'bg-green-500/20 text-green-400 border-green-500/30',
    member: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };
  return classes[role] || classes.member;
};


// ==================== USER DETAIL DRAWER ====================
const UserDetailDrawer = ({ userId, isOpen, onClose, session, onRefresh }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserDetail();
    }
  }, [isOpen, userId]);

  const fetchUserDetail = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/users/${userId}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      toast.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRole = async () => {
    if (!selectedOrg || !newRole) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          organization_id: selectedOrg.organization_id,
          new_role: newRole,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Role updated');
        setShowRoleDialog(false);
        fetchUserDetail();
        onRefresh?.();
      } else {
        toast.error(data.detail || 'Failed to change role');
      }
    } catch (error) {
      toast.error('Failed to change role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/users/${userId}/suspend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ reason: suspendReason }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'User suspended');
        setShowSuspendDialog(false);
        fetchUserDetail();
        onRefresh?.();
      } else {
        toast.error(data.detail || 'Failed to suspend user');
      }
    } catch (error) {
      toast.error('Failed to suspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/users/${userId}/reactivate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'User reactivated');
        fetchUserDetail();
        onRefresh?.();
      } else {
        toast.error(data.detail || 'Failed to reactivate user');
      }
    } catch (error) {
      toast.error('Failed to reactivate user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'User deleted');
        setShowDeleteDialog(false);
        onClose();
        onRefresh?.();
      } else {
        toast.error(data.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/users/${userId}/reset-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Password reset email sent');
      } else {
        toast.error(data.detail || 'Failed to send reset email');
      }
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setActionLoading(false);
    }
  };

  const handleImpersonate = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/users/${userId}/impersonate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Impersonation session created (logged in audit)');
        // In a real app, you would store the token and redirect
        console.log('Impersonation token:', data.impersonation_token);
      } else {
        toast.error(data.detail || 'Failed to impersonate');
      }
    } catch (error) {
      toast.error('Failed to impersonate');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} title="User Details" width="lg">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-electric-ice-blue" />
        </div>
      </Drawer>
    );
  }

  if (!user) return null;

  const profile = user.profile || {};
  const isSuspended = profile.is_suspended;
  const isDeleted = profile.deleted_at;

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} title="User Details" width="lg">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-xl bg-cool-mint/10 flex items-center justify-center">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="w-16 h-16 rounded-xl object-cover" />
              ) : (
                <User className="w-8 h-8 text-cool-mint" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-cloud-white">
                {profile.display_name || profile.email?.split('@')[0] || 'Unknown User'}
              </h3>
              <p className="text-soft-silver flex items-center gap-2 mt-1">
                <Mail className="w-4 h-4" />
                {profile.email}
              </p>
              <div className="flex items-center gap-2 mt-2">
                {isSuspended && (
                  <Badge className="bg-signal-red/20 text-signal-red border-signal-red/30">Suspended</Badge>
                )}
                {isDeleted && (
                  <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">Deleted</Badge>
                )}
                {profile.platform_role && (
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                    {profile.platform_role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-obsidian/50 rounded-lg p-4 border border-white/[0.08]">
              <p className="text-muted-text text-xs">Organizations</p>
              <p className="text-2xl font-bold text-cloud-white">{user.stats?.organization_count || 0}</p>
            </div>
            <div className="bg-obsidian/50 rounded-lg p-4 border border-white/[0.08]">
              <p className="text-muted-text text-xs">Events Attended</p>
              <p className="text-2xl font-bold text-cloud-white">{user.stats?.event_attendance_count || 0}</p>
            </div>
            <div className="bg-obsidian/50 rounded-lg p-4 border border-white/[0.08]">
              <p className="text-muted-text text-xs">President Of</p>
              <p className="text-2xl font-bold text-cloud-white">{user.stats?.is_president_of || 0}</p>
            </div>
          </div>

          {/* Organizations */}
          <div>
            <h4 className="text-sm font-semibold text-cloud-white mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Organization Memberships
            </h4>
            {user.organizations?.length > 0 ? (
              <div className="space-y-2">
                {user.organizations.map((org) => (
                  <div key={org.organization_id} className="flex items-center justify-between p-3 bg-obsidian/50 rounded-lg border border-white/[0.08]">
                    <div>
                      <p className="text-cloud-white font-medium">{org.organization_name}</p>
                      <p className="text-xs text-muted-text">{org.campus_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getRoleBadgeClass(org.role)}>
                        {org.role === 'owner' ? (
                          <><Crown className="w-3 h-3 mr-1" /> President</>
                        ) : (
                          getRoleDisplay(org.role)
                        )}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedOrg(org);
                          setNewRole(org.role);
                          setShowRoleDialog(true);
                        }}
                        className="text-soft-silver hover:text-cloud-white"
                      >
                        Change
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-text text-sm">Not a member of any organization</p>
            )}
          </div>

          {/* Actions */}
          <div className="border-t border-white/[0.08] pt-4">
            <h4 className="text-sm font-semibold text-cloud-white mb-3">Actions</h4>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                className="border-white/[0.08]"
                onClick={handleResetPassword}
                disabled={actionLoading}
              >
                <Key className="w-4 h-4 mr-2" />
                Reset Password
              </Button>
              <Button
                variant="outline"
                className="border-white/[0.08]"
                onClick={handleImpersonate}
                disabled={actionLoading || isSuspended}
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Impersonate
              </Button>
              {isSuspended ? (
                <Button
                  variant="outline"
                  className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                  onClick={handleReactivate}
                  disabled={actionLoading}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reactivate
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  onClick={() => setShowSuspendDialog(true)}
                  disabled={actionLoading}
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Suspend
                </Button>
              )}
              <Button
                variant="outline"
                className="border-signal-red/30 text-signal-red hover:bg-signal-red/10"
                onClick={() => setShowDeleteDialog(true)}
                disabled={actionLoading || isDeleted}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>

          {/* Recent Activity */}
          {user.recent_activity?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-cloud-white mb-3">Recent Activity</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {user.recent_activity.slice(0, 10).map((activity, idx) => (
                  <div key={idx} className="text-xs text-muted-text flex justify-between">
                    <span>{activity.action?.replace('_', ' ')}</span>
                    <span>{formatDate(activity.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Drawer>

      {/* Change Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="bg-graphite border-white/[0.08]">
          <DialogHeader>
            <DialogTitle className="text-cloud-white">Change Role</DialogTitle>
            <DialogDescription className="text-soft-silver">
              Change role for {profile.display_name || profile.email} in {selectedOrg?.organization_name}
            </DialogDescription>
          </DialogHeader>
          <Select value={newRole} onValueChange={setNewRole}>
            <SelectTrigger className="bg-obsidian border-white/[0.08]">
              <SelectValue placeholder="Select role" />
            </SelectTrigger>
            <SelectContent className="bg-graphite border-white/[0.08]">
              <SelectItem value="owner">President (Owner)</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="event_manager">Event Manager</SelectItem>
              <SelectItem value="door_staff">Door Staff</SelectItem>
              <SelectItem value="member">Member</SelectItem>
            </SelectContent>
          </Select>
          {newRole === 'owner' && selectedOrg?.role !== 'owner' && (
            <p className="text-xs text-amber-400 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              This will demote the current President to Admin
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>Cancel</Button>
            <Button onClick={handleChangeRole} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent className="bg-graphite border-white/[0.08]">
          <DialogHeader>
            <DialogTitle className="text-cloud-white">Suspend User</DialogTitle>
            <DialogDescription className="text-soft-silver">
              This will prevent {profile.display_name || profile.email} from logging in.
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Reason for suspension (optional)"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            className="bg-obsidian border-white/[0.08]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuspendDialog(false)}>Cancel</Button>
            <Button onClick={handleSuspend} disabled={actionLoading} className="bg-amber-500 text-black hover:bg-amber-600">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Suspend User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-graphite border-white/[0.08]">
          <DialogHeader>
            <DialogTitle className="text-cloud-white">Delete User</DialogTitle>
            <DialogDescription className="text-soft-silver">
              This will permanently delete {profile.display_name || profile.email}'s account and remove them from all organizations.
            </DialogDescription>
          </DialogHeader>
          <p className="text-signal-red text-sm">This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={actionLoading} className="bg-signal-red text-white hover:bg-signal-red/90">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};


// ==================== MAIN USERS PAGE ====================
export const Users = () => {
  const { session } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Fetch users
  const fetchUsers = useCallback(async (status = null) => {
    if (!session?.access_token) return;
    setLoading(true);
    try {
      let url = `${API_URL}/api/platform/users?page_size=100`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, search]);

  useEffect(() => {
    if (activeTab === 'all') fetchUsers();
    else if (activeTab === 'suspended') fetchUsers('suspended');
    else if (activeTab === 'deleted') fetchUsers('deleted');
  }, [activeTab, fetchUsers]);

  // Filter users
  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.display_name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleViewUser = (userId) => {
    setSelectedUserId(userId);
    setIsDetailOpen(true);
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-electric-ice-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-cloud-white">User Management</h1>
          <p className="text-soft-silver text-sm mt-1">{users.length} users across all organizations</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList className="bg-obsidian/50 border border-white/[0.08]">
            <TabsTrigger value="all" data-testid="tab-all-users">All Users</TabsTrigger>
            <TabsTrigger value="suspended" data-testid="tab-suspended">Suspended</TabsTrigger>
            <TabsTrigger value="deleted" data-testid="tab-deleted">Deleted</TabsTrigger>
          </TabsList>
          
          {/* Search */}
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-obsidian border-white/[0.08]"
              data-testid="user-search-input"
            />
          </div>
        </div>

        <TabsContent value={activeTab} className="mt-4">
          {filteredUsers.length === 0 ? (
            <EmptyState
              icon="users"
              title="No users found"
              description={search ? "Try a different search term" : "Users will appear here once they sign up"}
            />
          ) : (
            <div className="bg-obsidian/50 border border-white/[0.08] rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.08]">
                    <th className="text-left text-xs font-medium text-muted-text uppercase tracking-wider px-4 py-3">User</th>
                    <th className="text-left text-xs font-medium text-muted-text uppercase tracking-wider px-4 py-3">Email</th>
                    <th className="text-left text-xs font-medium text-muted-text uppercase tracking-wider px-4 py-3">Organizations</th>
                    <th className="text-left text-xs font-medium text-muted-text uppercase tracking-wider px-4 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-muted-text uppercase tracking-wider px-4 py-3">Joined</th>
                    <th className="text-right text-xs font-medium text-muted-text uppercase tracking-wider px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-electric-ice-blue/20 flex items-center justify-center">
                            {user.avatar_url ? (
                              <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <span className="text-sm font-medium text-electric-ice-blue">
                                {(user.display_name || user.email)?.[0]?.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <span className="text-cloud-white font-medium">
                            {user.display_name || user.email?.split('@')[0]}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-soft-silver">{user.email}</td>
                      <td className="px-4 py-3">
                        {user.organizations?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {user.organizations.slice(0, 2).map((org, idx) => (
                              <Badge key={idx} className={cn("text-xs", getRoleBadgeClass(org.role))}>
                                {org.role === 'owner' && <Crown className="w-3 h-3 mr-1" />}
                                {org.organization_name?.slice(0, 15)}
                              </Badge>
                            ))}
                            {user.organizations.length > 2 && (
                              <Badge className="bg-white/10 text-soft-silver text-xs">
                                +{user.organizations.length - 2}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-text text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.is_suspended ? (
                          <Badge className="bg-signal-red/20 text-signal-red border-signal-red/30">Suspended</Badge>
                        ) : user.deleted_at ? (
                          <Badge className="bg-gray-500/20 text-gray-400">Deleted</Badge>
                        ) : (
                          <Badge className="bg-fresh-mint/20 text-fresh-mint border-fresh-mint/30">Active</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-soft-silver text-sm">{formatDate(user.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-soft-silver hover:text-cloud-white">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-graphite border-white/[0.08]">
                            <DropdownMenuItem onClick={() => handleViewUser(user.id)} className="text-cloud-white">
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-white/[0.08]" />
                            <DropdownMenuItem className="text-soft-silver">
                              <Key className="w-4 h-4 mr-2" />
                              Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-soft-silver">
                              <UserCheck className="w-4 h-4 mr-2" />
                              Impersonate
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* User Detail Drawer */}
      <UserDetailDrawer
        userId={selectedUserId}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        session={session}
        onRefresh={() => fetchUsers(activeTab === 'all' ? null : activeTab)}
      />
    </div>
  );
};

export default Users;
